import {
  ICombinedSimpleModel,
  ISimpleFilterModel,
} from '@ag-grid-community/core'
import { toUtcTime } from '@helios/utils'
import { ScalerFilterModel } from '@northfork/ag-grid-angular'
import { FilterType, MultiFilter, SingleFilter } from './filter'

export interface TimeFilter extends MultiFilter<string> {
  filterType: FilterType.Time
}

type TimeFilterOperator =
  | 'equals'
  | 'lessThan'
  | 'lessThanOrEqual'
  | 'greaterThan'
  | 'greaterThanOrEqual'
  | 'inRange'

interface SingleTimeFilter extends SingleFilter<string> {
  operator: TimeFilterOperator
  value?: string
  // populated if the operator is 'inRange'
  range?: Range
}

interface Range {
  from: string
  to: string
}

/**
 * Check if the date filter operator is in the defined list
 * defined here: https://stash.arbfund.com/projects/HEL/repos/helios/browse/libs/request-model/src/main/kotlin/com/clearwateranalytics/helios/requests/filter/DateFilter.kt#49-57
 */
function isTimeFilterOperator(s: string): s is TimeFilterOperator {
  return [
    'equals',
    'lessThan',
    'lessThanOrEqual',
    'greaterThan',
    'greaterThanOrEqual',
    'inRange',
  ].includes(s)
}

/**
 * Convert combined into a time filter
 */
export function convertICombinedSimpleModelToTimeFilter(
  combinedFilter: ICombinedSimpleModel<ISimpleFilterModel>,
): TimeFilter | null {
  if (
    combinedFilter.condition1.filterType !== FilterType.Time ||
    combinedFilter.condition2.filterType !== FilterType.Time
  ) {
    console.error('Filter type should be time', combinedFilter)
    return null
  }
  const firstFilterModel =
    combinedFilter.condition1 as ScalerFilterModel<string>
  const firstSingleFilter = convertSingleFilter(firstFilterModel)

  const secondFilterModel =
    combinedFilter.condition2 as ScalerFilterModel<string>
  const secondSingleFilter = convertSingleFilter(secondFilterModel)

  if (firstSingleFilter && secondSingleFilter) {
    return {
      operator: combinedFilter.operator,
      filterType: FilterType.Time,
      filters: [firstSingleFilter, secondSingleFilter],
    }
  } else {
    return null
  }
}

/**
 * Convert simple filter to a time filter
 */
export function convertISimpleFilterModelToTimeFilter(
  filter: ISimpleFilterModel,
): TimeFilter | null {
  if (filter.filterType !== FilterType.Time) {
    console.error('Filter type should be time', filter)
    return null
  }

  const filterModel = filter as ScalerFilterModel<string> //Not sure why this is not DateFilterModel, but I have tested it ag-grid always sends ScalerFilterModel
  const singleFilter = convertSingleFilter(filterModel)
  if (singleFilter) {
    return {
      operator: 'AND',
      filterType: FilterType.Time,
      filters: [singleFilter],
    }
  } else {
    return null
  }
}

/**
 * Convert scalar filter to a simple filter
 */
function convertSingleFilter(filterModel: ScalerFilterModel<string>) {
  if (filterModel.type && isTimeFilterOperator(filterModel.type)) {
    const singleTimeFilter: SingleTimeFilter = {
      operator: filterModel.type,
    }

    if (filterModel.type === 'inRange') {
      if (filterModel.filter && filterModel.filterTo) {
        singleTimeFilter.range = {
          from: detectAndConvertTime(filterModel.filter),
          to: detectAndConvertTime(filterModel.filterTo),
        }
      } else {
        console.error('inRange requires filter and filterTo', filterModel)
        return null
      }
    } else {
      if (filterModel.filter) {
        singleTimeFilter.value = detectAndConvertTime(filterModel.filter)
      } else {
        console.error('Missing filter', filterModel)
        return null
      }
    }
    return singleTimeFilter
  } else {
    console.error('Filter operator not supported', filterModel)
    return null
  }
}

/**
 * Detect and extract the time from a datetime string
 */
function detectAndConvertTime(value: string): string {
  if (!value) {
    return value
  }

  const results = toUtcTime(value)
  return results
}
